<template>
  <div class="planning-item-summary-container" :class="{'has-summary': planningItem.summary && doesCurrentPlanSupportSummary}">
    <table class="planning-item-details-container" ref="planningItemDetailsContainer">
      <template v-if="planningItem.isLead || planningItem.isNested">
        <tr>
          <td colspan="2">
            <template v-if="planningItem.isLead"><span class="house-icon"></span></template>
            <template v-else-if="currentPlan.isLead">{{ planningItem.abbreviatedPlanName }}</template>
          </td>
        </tr>
      </template>
      <template v-if="planningItem.isInitiative || planningItem.isInitiativeCustomization">
        <tr>
          <td>Name:</td>
          <td>{{planningItem.title}}</td>
        </tr>
        <tr>
          <td>Category:</td>
          <td>{{planningItemTypeName}}</td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td>Topic:</td>
          <td>{{planningItem.title}}</td>
        </tr>
        <tr>
          <td>Channel:</td>
          <td>{{planningItem.channelName}}</td>
        </tr>
        <tr>
          <td>Tactic Type:</td>
          <td>{{planningItemTypeName}}</td>
        </tr>
        <tr v-if="planningItem.tacticPlatforms && planningItem.tacticPlatforms.length">
          <td>Platform:</td>
          <td>{{planningItemPlatformsString}}</td>
        </tr>
      </template>
      <template v-if="planningItem.rrule != null">
        <tr>
          <td v-if="planningItem.startDate?.toString() !== planningItem.endDate?.toString()">Start Date:</td>
          <td v-else>Date:</td>
          <td>{{planningItemStartDate}}</td>
        </tr>
        <tr v-if="planningItem.startDate?.toString() !== planningItem.endDate?.toString()">
          <td>End Date:</td>
          <td>{{planningItemEndDate}}</td>
        </tr>
        <tr>
          <td>Repeats:</td>
          <td>{{repeatsDisplay}}</td>
        </tr>
        <tr>
          <td>End Repeat:</td>
          <td>{{planningItem.recurrenceEndDate ? planningItem.recurrenceEndDate.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'}) : 'Never'}}</td>
        </tr>
      </template>
      <template v-else-if="planningItemStartDate == planningItemEndDate">
        <tr>
          <td>Date:</td>
          <td>{{planningItemStartDate}}</td>
        </tr>
      </template>
      <template v-else>
        <tr>
          <td>Start Date:</td>
          <td>{{planningItemStartDate}}</td>
        </tr>
        <tr>
          <td>End Date:</td>
          <td>{{planningItemEndDate}}</td>
        </tr>
      </template>
    </table>
    <template v-if="hasSummaryContent && doesCurrentPlanSupportSummary">
      <Skeleton width="100%" height="5rem" v-show="shouldShowSummaryLoadingSkeleton"></Skeleton>
      <table class="notes-container" ref="notesContainer" v-show="!shouldShowSummaryLoadingSkeleton">
        <tr>
          <td>{{ planningItem.isInitiativeCustomization ? 'Lead Initiative Notes:' : 'Notes:' }}</td>
        </tr>
        <tr>
          <td>
            <div class="notes-field-content-container" v-html="planningItem.isInitiativeCustomization ? planningItem.leadInitiativeSummary : planningItem.summary"></div>
          </td>
        </tr>
      </table>
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import PlanningItem from '@/models/InterfacePlanningItem'
import {format, isSameDay} from 'date-fns'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Skeleton from 'primevue/skeleton';
import Plan from '@/models/Plan';
import { getDtStart, stripExDates } from '@/utils/recurrence'

Vue.component('Skeleton', Skeleton)

export default Vue.extend({
  name: 'PlanningItemSummary',
  props: {
    planningItemId: {
      type: Object,
    },
    instanceStartDate: {
      type: Date,
      default: null,
    },
    instanceEndDate: {
      type: Date,
      default: null,
    },
  },
  data: ()=>{
    return {
      planningItem: {} as PlanningItem,
      shouldShowSummaryLoadingSkeleton: true as boolean,
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    planningItemTypeName: function(): string {
      if(this.planningItem.isInitiative || this.planningItem.isInitiativeCustomization){
        return this.planningItem.initiativeTypeName || ''
      }
      return this.planningItem.tacticTypeName || ''
    },
    dateOptions: function(): object {
      if(this.planningItem.isAllDay || this.planningItem.isInitiative || this.planningItem.isInitiativeCustomization || this.planningItemTypeName.toLowerCase() === this.$store.getters.monthlyFocusTypeName){
        return {year: 'numeric', month: 'long', day: 'numeric'}
      }
      return {year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric'}
    },
    planningItemStartDate: function(): string {
      const instanceDate = this.instanceStartDate as unknown as Date | null
      const baseDate = this.planningItem.startDate
      if (!baseDate) return ''
      if (instanceDate) {
        const displayDate = new Date(instanceDate)
        displayDate.setHours(baseDate.getHours(), baseDate.getMinutes(), baseDate.getSeconds(), 0)
        return displayDate.toLocaleDateString('en-US', this.dateOptions)
      }
      return baseDate.toLocaleDateString('en-US', this.dateOptions)
    },
    planningItemEndDate: function(): string {
      const instanceDate = this.instanceEndDate as unknown as Date | null
      const baseDate = this.planningItem.endDate
      if (!baseDate) return ''
      if (instanceDate) {
        const displayDate = new Date(instanceDate)
        displayDate.setHours(baseDate.getHours(), baseDate.getMinutes(), baseDate.getSeconds(), 0)
        return displayDate.toLocaleDateString('en-US', this.dateOptions)
      }
      return baseDate.toLocaleDateString('en-US', this.dateOptions)
    },
    doesCurrentPlanSupportSummary: function(): boolean {
      return this.currentPlan.isTacticSummaryFieldEnabled
    },
    hasSummaryContent(): boolean {
      if (this.planningItem.isInitiativeCustomization) {
        return !!(this.planningItem.leadInitiativeSummaryContentUrl || this.planningItem.leadInitiativeSummary)
      } else if (this.planningItem.isInitiative) {
        return !!(this.planningItem.initiativeSummary?.contentUrl || this.planningItem.summary)
      } else {
        return !!(this.planningItem.tacticSummary?.contentUrl || this.planningItem.summary)
      }
    },
    planningItemPlatformsString(): string {
      const platformNames = [] as string[]
      if(this.planningItem.tacticPlatforms){
        this.planningItem.tacticPlatforms.forEach((platform)=>{
          const thisPlatform = this.$store.getters.currentTacticPlatforms.find((planPlatform)=>planPlatform.id.intID == platform.id.intID)
          if(thisPlatform){
            platformNames.push(thisPlatform.name)
          }
        })
      }
      return platformNames.join(', ')
    },
    // The date the "Repeats on" preset candidates are built from. Must match the persisted
    // rrule's own DTSTART exactly for repeatsDisplay to find a matching preset label (see
    // repeatsDisplay below) — planningItem.startDate is NOT reliable for that: it's
    // independently normalized to local noon for display (see parseAllDayDate), which doesn't
    // necessarily match the wall-clock time the rrule's own DTSTART was originally created with.
    // Read it straight back out of the rrule text when present (mirrors TacticDetail.vue's
    // recurrenceAnchorDate).
    recurrenceAnchorDate(): Date {
      const persistedDtStart = this.planningItem.rrule ? getDtStart(this.planningItem.rrule) : null
      return persistedDtStart ?? new Date(this.planningItem.startDate)
    },
    repeatOptions(): object[] {
      // TODO: Copied from src/views/TacticDetail.vue, consider combining in to src/AppStore.ts
      const startDate = this.recurrenceAnchorDate
      const year = startDate.getFullYear()
      const month =
        startDate.getMonth() > 8
          ? startDate.getMonth() + 1
          : '0' + (startDate.getMonth() + 1)
      const date =
        startDate.getDate() > 9
          ? startDate.getDate()
          : '0' + startDate.getDate()
      const hours =
        startDate.getHours() > 9
          ? startDate.getHours()
          : '0' + startDate.getHours()
      const minutes =
        startDate.getMinutes() > 9
          ? startDate.getMinutes()
          : '0' + startDate.getMinutes()
      const seconds =
        startDate.getSeconds() > 9
          ? startDate.getSeconds()
          : '0' + startDate.getSeconds()

      const dtStart = this.planningItem.startDate
        ? `DTSTART=${year}${month}${date}T${hours}${minutes}${seconds};`
        : ''
      return [
        {
          value: `${dtStart}FREQ=DAILY`,
          label: 'Daily',
        },
        {
          value: `${dtStart}FREQ=WEEKLY;WKST=SU;BYDAY=${format(
            startDate,
            'EEEEEE'
          ).toUpperCase()}`,
          label: `Weekly on ${format(startDate, 'EEEE')}`,
        },
        {
          value: `${dtStart}FREQ=MONTHLY;BYMONTHDAY=${format(
            startDate,
            'd'
          )}`,
          label: `Monthly on the ${format(startDate, 'do')}`,
        },
        {
          ...this.getMonthlyByDay(startDate, dtStart),
        },
        {
          value: `${dtStart}FREQ=YEARLY`,
          label: `Annually on ${format(startDate, 'MMM')} ${format(
            startDate,
            'd'
          )}`,
        },
        {
          value: 'custom',
          label: 'Custom',
        },
      ]
    },
    repeatsDisplay(): string {
      if (this.planningItem.isCustomRecurrence) {
        return this.planningItem.recurrenceText || 'Custom'
      }
      // Strip any EXDATE tail (added when breaking an instance out of this series) before
      // comparing — repeatOptions' values never include one, so a tactic broken off from would
      // otherwise never match a preset and fall back to the unhelpful 'Custom' label.
      const rruleWithoutExDates = stripExDates(this.planningItem.rrule || '')
      const endDatePosition = rruleWithoutExDates.indexOf(';UNTIL')
      const rruleWithoutEndDate = endDatePosition > -1 ? rruleWithoutExDates.substring(0, endDatePosition) : rruleWithoutExDates
      const thisValue = this.repeatOptions.find((option)=>option['value'] == rruleWithoutEndDate)
      return thisValue ? thisValue['label'] : 'Custom'
    }
  },
  created: function(){
    // Try to find the planning item in the store
    let planningItemInStore: PlanningItem | undefined

    // Check if it's a tactic
    planningItemInStore = this.currentPlan.tactics.find((tactic)=>tactic.id.intID === this.planningItemId?.intID)

    // Check if it's an initiative
    if(!planningItemInStore){
      planningItemInStore = this.currentPlan.initiatives.find((initiative)=>initiative.id.intID === this.planningItemId?.intID)
    }

    // Check if it's an initiative customization
    if(!planningItemInStore){
      this.currentPlan.initiatives.forEach((initiative) => {
        if(!planningItemInStore){
          planningItemInStore = initiative.initiativeCustomizations?.find((customization)=>customization.id.intID === this.planningItemId?.intID)
        }
      })
    }

    this.planningItem = planningItemInStore ? planningItemInStore : {} as PlanningItem

    // Determine the S3 content URL to fetch from, if any
    let contentUrl: string | null | undefined = null
    let targetField: 'summary' | 'leadInitiativeSummary' = 'summary'

    if (this.planningItem.isInitiativeCustomization) {
      contentUrl = this.planningItem.leadInitiativeSummaryContentUrl
      targetField = 'leadInitiativeSummary'
    } else if (this.planningItem.isInitiative) {
      contentUrl = this.planningItem.initiativeSummary?.contentUrl
    } else {
      contentUrl = this.planningItem.tacticSummary?.contentUrl
    }

    const alreadyLoaded = targetField === 'leadInitiativeSummary'
      ? (this.planningItem.leadInitiativeSummary && this.planningItem.leadInitiativeSummary !== '<p></p>')
      : (this.planningItem.summary && this.planningItem.summary !== '<p></p>')

    if (contentUrl && !alreadyLoaded) {
      const fetchPromise: Promise<string> = this.planningItem.isInitiative || this.planningItem.isInitiativeCustomization
        ? this.$store.getters.services.initiatives.fetchSummaryContent(contentUrl)
        : this.$store.getters.services.tactics.fetchSummaryContent(contentUrl)

      fetchPromise.then(
        (html) => {
          if (targetField === 'leadInitiativeSummary') {
            this.planningItem.leadInitiativeSummary = html
          } else {
            this.planningItem.summary = html
          }
          this.shouldShowSummaryLoadingSkeleton = false
        },
        () => {
          this.shouldShowSummaryLoadingSkeleton = false
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'Error fetching summary.',
            life: 3000,
          })
        }
      )
    } else {
      this.shouldShowSummaryLoadingSkeleton = false
    }
  },
  methods: {
    getMonthlyByDay(
      startDate: Date,
      dtStart: string,
      onlyReturnMonthlyRule = false
    ) {
      // TODO: Copied from src/views/TacticDetail.vue, consider combining in to src/AppStore.ts
      const d = new Date(startDate.getTime())
      const dayOfWeek = d.getDay()
      const month = d.getMonth()
      const datesWithSameDayOfWeek = [] as Date[]

      // Get the first instance of the day of week in the month
      d.setDate(1)
      while (d.getDay() !== dayOfWeek) {
        d.setDate(d.getDate() + 1)
      }

      // get all instances of day of week
      while (d.getMonth() === month) {
        datesWithSameDayOfWeek.push(new Date(d))
        d.setDate(d.getDate() + 7)
      }

      // find our start date's index of those
      let indexOfStartDate = 0
      datesWithSameDayOfWeek.forEach((date, index) => {
        if (isSameDay(startDate, date)) {
          indexOfStartDate = index
        }
      })

      const isFifthInstanceOfWeekDay = indexOfStartDate > 4

      if (onlyReturnMonthlyRule) {
        return {
          value: isFifthInstanceOfWeekDay
            ? `BYSETPOS=-1;BYDAY=${format(startDate, 'EEEEEE').toUpperCase()}`
            : `BYSETPOS=${indexOfStartDate + 1};BYDAY=${format(
                startDate,
                'EEEEEE'
              ).toUpperCase()}`,
          label: isFifthInstanceOfWeekDay
            ? `Monthly on last ${format(startDate, 'eeee')} of the month`
            : `The ${indexOfStartDate + 1}${
                ['st', 'nd', 'rd'][
                  ((((indexOfStartDate + 1 + 90) % 100) - 10) % 10) - 1
                ] || 'th'
              } ${format(startDate, 'eeee')} of the month`,
        }
      }
      return {
        value: isFifthInstanceOfWeekDay
          ? `${dtStart}FREQ=MONTHLY;BYSETPOS=-1;BYDAY=${format(
              startDate,
              'EEEEEE'
            ).toUpperCase()}`
          : `${dtStart}FREQ=MONTHLY;BYSETPOS=${
              indexOfStartDate + 1
            };BYDAY=${format(startDate, 'EEEEEE').toUpperCase()}`,
        label: isFifthInstanceOfWeekDay
          ? `Monthly on last ${format(startDate, 'eeee')} of the month`
          : `Monthly on the ${indexOfStartDate + 1}${
              ['st', 'nd', 'rd'][
                ((((indexOfStartDate + 1 + 90) % 100) - 10) % 10) - 1
              ] || 'th'
            } ${format(startDate, 'eeee')} of the month`,
      }
    },
  }
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.planning-item-summary-overlay {
  pointer-events: none;

  // Panel above planning item, offset so caret aligns with top of planning item box
  &.p-overlaypanel-flipped {
    transform: translateY(-10px);
  }
}
.planning-item-summary-container {
  min-width: 20rem;
  max-width: 30rem;
  height: auto;

  .planning-item-details-container {
    tr {
      td:first-of-type {
        min-width: 8rem;
        padding-right: 1rem;
      }
    }
  }
  .notes-container {
    width: 100%;
  }
  .notes-field-content-container {
    width: 100%;
    max-width: 30rem;
    max-height: 400px;
    overflow: hidden;
  }
  .house-icon::after {
    content: "\e925";
    display: block;
    font-family: "primeicons";
    font-size: 1.6rem;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
  }
}
</style>
